"use client";

import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    MapPin,
    Building2,
    Flag,
    X,
    HelpCircle,
    ExternalLink,
} from "lucide-react";
import { useLocationSearch, type LocationSuggestion } from "../../hooks/useLocationSearch";
import { getLocationInfo } from "../../lib/locationMapping";

function useDebounce<T>(value: T, delay: number = 500): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}

function useRotatingPlaceholder(placeholders: string[], interval: number = 3000): string {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % placeholders.length);
        }, interval);

        return () => clearInterval(timer);
    }, [placeholders.length, interval]);

    return placeholders[currentIndex];
}

function useTypingEffect(text: string, speed: number = 50): string {
    const [displayedText, setDisplayedText] = useState("");

    useEffect(() => {
        setDisplayedText("");
        let currentIndex = 0;

        const timer = setInterval(() => {
            if (currentIndex <= text.length) {
                setDisplayedText(text.slice(0, currentIndex));
                currentIndex++;
            } else {
                clearInterval(timer);
            }
        }, speed);

        return () => clearInterval(timer);
    }, [text, speed]);

    return displayedText;
}

export interface Action {
    id: string;
    label: string;
    icon: React.ReactNode;
    description?: string;
    short?: string;
    end?: string;
}

interface ActionSearchBarProps {
    onLocationSelect?: (location: LocationSuggestion | null) => void;
    selectedLocation?: LocationSuggestion | null;
}

function ActionSearchBar({ onLocationSelect, selectedLocation: externalSelectedLocation }: ActionSearchBarProps) {
    const [query, setQuery] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<LocationSuggestion | null>(null);
    const [hasSelectedLocation, setHasSelectedLocation] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [showHelpPopover, setShowHelpPopover] = useState(false);
    const debouncedQuery = useDebounce(query, 200);
    const { suggestions, loading } = useLocationSearch(debouncedQuery);

    const getFullLocationName = (location: LocationSuggestion): string => {
        // For cities, show: "City, State, Country" or "City, Country"
        if (location.type === 'city') {
            if (location.state) {
                return `${location.name}, ${location.state}, ${location.country}`;
            }
            return `${location.name}, ${location.country}`;
        }
        // For states, show: "State, Country"
        if (location.type === 'state') {
            return `${location.name}, ${location.country}`;
        }
        // For countries, just show the country name
        return location.name;
    };

    // Sync with external selected location
    useEffect(() => {
        if (externalSelectedLocation) {
            setSelectedLocation(externalSelectedLocation);
            setQuery(getFullLocationName(externalSelectedLocation));
            setHasSelectedLocation(true);
        } else if (externalSelectedLocation === null) {
            // Clear if external is explicitly null
            setSelectedLocation(null);
            setQuery("");
            setHasSelectedLocation(false);
        }
    }, [externalSelectedLocation]);

    const placeholder = "Add your location";

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setQuery(newValue);

        // Only clear selection state if the query is being modified
        if (hasSelectedLocation && newValue !== selectedLocation?.name) {
            setHasSelectedLocation(false);
            setSelectedLocation(null);
            onLocationSelect?.(null);
            setIsFocused(true); // Ensure dropdown shows when editing selected location
        }

        setHighlightedIndex(-1);
    };

    const handleLocationSelect = (location: LocationSuggestion) => {
        if (!location) return;

        setSelectedLocation(location);
        setQuery(getFullLocationName(location));
        setHasSelectedLocation(true);
        setIsFocused(false);
        setHighlightedIndex(-1);
        onLocationSelect?.(location);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // If not focused, do nothing
        if (!isFocused) return;

        // If we have no suggestions and user presses Enter, ignore it
        // (Don't create fake locations from invalid input)
        if (suggestions.length === 0 && e.key === 'Enter') {
            e.preventDefault();
            return;
        }

        // If we have no suggestions, ignore all other keys
        if (suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((prev) =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                    handleLocationSelect(suggestions[highlightedIndex]);
                } else if (suggestions.length > 0) {
                    handleLocationSelect(suggestions[0]);
                } else if (query.trim()) {
                    const locationInfo = getLocationInfo(query.trim());
                    if (locationInfo) {
                        handleLocationSelect(locationInfo as LocationSuggestion);
                    }
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsFocused(false);
                setHighlightedIndex(-1);
                break;
        }
    };

    const getLocationIcon = (type: string) => {
        switch (type) {
            case 'city':
                return <Building2 className="w-4 h-4 text-gray-500" />;
            case 'state':
                return <MapPin className="w-4 h-4 text-gray-500" />;
            case 'country':
                return <Flag className="w-4 h-4 text-gray-500" />;
            default:
                return <MapPin className="w-4 h-4 text-gray-500" />;
        }
    };

    const getLocationTypeLabel = (type: string) => {
        switch (type) {
            case 'city':
                return 'City';
            case 'state':
                return 'State/Province';
            case 'country':
                return 'Country';
            default:
                return '';
        }
    };

    const getCountryRegion = (countryCode: string | undefined): string => {
        if (!countryCode) return '';

        const regions: Record<string, string> = {
            // North America
            'US': 'North America', 'CA': 'North America', 'MX': 'North America',
            // Latin America (Central America, Caribbean, South America)
            'CR': 'Latin America', 'PA': 'Latin America', 'GT': 'Latin America',
            'HN': 'Latin America', 'NI': 'Latin America', 'SV': 'Latin America',
            'BZ': 'Latin America', 'JM': 'Latin America', 'TT': 'Latin America',
            'BB': 'Latin America', 'DO': 'Latin America', 'CU': 'Latin America',
            'BR': 'Latin America', 'AR': 'Latin America', 'CL': 'Latin America',
            'CO': 'Latin America', 'PE': 'Latin America', 'VE': 'Latin America',
            'EC': 'Latin America', 'BO': 'Latin America', 'PY': 'Latin America',
            'UY': 'Latin America', 'GY': 'Latin America', 'SR': 'Latin America',
            // Europe (all European countries)
            'GB': 'Europe', 'IE': 'Europe', 'FR': 'Europe', 'DE': 'Europe',
            'ES': 'Europe', 'IT': 'Europe', 'PT': 'Europe', 'NL': 'Europe',
            'BE': 'Europe', 'LU': 'Europe', 'CH': 'Europe', 'AT': 'Europe',
            'DK': 'Europe', 'SE': 'Europe', 'NO': 'Europe', 'FI': 'Europe', 'IS': 'Europe',
            'PL': 'Europe', 'CZ': 'Europe', 'SK': 'Europe', 'HU': 'Europe',
            'RO': 'Europe', 'BG': 'Europe', 'EE': 'Europe', 'LV': 'Europe',
            'LT': 'Europe', 'BY': 'Europe', 'UA': 'Europe', 'MD': 'Europe',
            'GR': 'Europe', 'HR': 'Europe', 'SI': 'Europe', 'RS': 'Europe',
            'BA': 'Europe', 'MK': 'Europe', 'AL': 'Europe',
            // APAC (Asia + Pacific)
            'CN': 'APAC', 'JP': 'APAC', 'KR': 'APAC', 'TW': 'APAC',
            'HK': 'APAC', 'MN': 'APAC', 'SG': 'APAC', 'MY': 'APAC',
            'TH': 'APAC', 'VN': 'APAC', 'PH': 'APAC', 'ID': 'APAC',
            'MM': 'APAC', 'KH': 'APAC', 'LA': 'APAC', 'IN': 'APAC',
            'PK': 'APAC', 'BD': 'APAC', 'LK': 'APAC', 'NP': 'APAC',
            'AU': 'APAC', 'NZ': 'APAC', 'PG': 'APAC', 'FJ': 'APAC',
            // Middle East
            'AE': 'Middle East', 'SA': 'Middle East', 'IL': 'Middle East',
            'TR': 'Middle East', 'EG': 'Middle East', 'JO': 'Middle East',
            'QA': 'Middle East', 'KW': 'Middle East', 'OM': 'Middle East',
            'BH': 'Middle East', 'LB': 'Middle East', 'IQ': 'Middle East',
            // Africa
            'ZA': 'Africa', 'NG': 'Africa', 'KE': 'Africa', 'GH': 'Africa',
            'MA': 'Africa', 'TN': 'Africa', 'DZ': 'Africa', 'ET': 'Africa',
            'UG': 'Africa', 'TZ': 'Africa'
        };

        return regions[countryCode] || '';
    };

    const container = {
        hidden: { opacity: 0, scale: 0.95 },
        show: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.15,
                ease: [0.4, 0.0, 0.2, 1],
            },
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            transition: {
                duration: 0.1,
            },
        },
    };

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.3,
            },
        },
        exit: {
            opacity: 0,
            y: -10,
            transition: {
                duration: 0.2,
            },
        },
    };

    // Focus handler
    const handleFocus = () => {
        setIsFocused(true);
        // If user clicks on the input after selecting a location, allow them to search again
        if (hasSelectedLocation) {
            setHasSelectedLocation(false);
        }
    };

    // Clear location handler
    const handleClearLocation = () => {
        setSelectedLocation(null);
        setQuery("");
        setHasSelectedLocation(false);
        onLocationSelect?.(null);
    };

    return (
        <div className="w-full">
            <div className="relative flex flex-col justify-start items-start">
                <div className="w-full sticky top-0 bg-background z-10">
                    <div className="relative">
                        {/* Search icon on the left side (always visible) */}
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <Search className="w-4 h-4 text-gray-700" />
                        </div>

                        {/* Right side button (help or clear) */}
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
                            <AnimatePresence mode="wait">
                                {hasSelectedLocation ? (
                                    <motion.button
                                        key="clear"
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0, opacity: 0 }}
                                        transition={{ duration: 0.15 }}
                                        onClick={handleClearLocation}
                                        className="flex items-center justify-center cursor-pointer hover:text-gray-900 transition-colors"
                                        type="button"
                                    >
                                        <X className="w-4 h-4 text-gray-400" />
                                    </motion.button>
                                ) : (
                                    <motion.button
                                        key="help"
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0, opacity: 0 }}
                                        transition={{ duration: 0.15 }}
                                        type="button"
                                        onClick={() => setShowHelpPopover(!showHelpPopover)}
                                        className="flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <HelpCircle className="w-4 h-4" />
                                    </motion.button>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Help popover */}
                        <AnimatePresence>
                            {showHelpPopover && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                    className="absolute top-full left-0 right-0 sm:left-0 sm:right-auto mt-2 w-full sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-3 sm:p-4 z-50"
                                >
                                    <div className="space-y-3">
                                        <div className="flex items-start justify-between">
                                            <h3 className="text-sm font-semibold text-gray-900">
                                                How does location search work?
                                            </h3>
                                            <button
                                                onClick={() => setShowHelpPopover(false)}
                                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="text-sm text-gray-600 leading-relaxed space-y-2">
                                            <p>We'll show you all jobs available to you.</p>
                                            <p>For example, if you select London, UK as your location, you will be shown remote jobs hiring globally, in Europe or in the United Kingdom, as well as onsite and hybrid jobs hiring in London.</p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <Input
                            id="location-search"
                            type="text"
                            value={query}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            onFocus={handleFocus}
                            onBlur={() =>
                                setTimeout(() => setIsFocused(false), 200)
                            }
                            placeholder={placeholder}
                            className="h-auto pl-9 sm:pl-10 pr-9 sm:pr-10 py-1.5 sm:py-2 text-xs sm:text-sm !text-gray-700 rounded-lg focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:ring-offset-0 border border-gray-200 focus:border-gray-300 bg-white placeholder:transition-opacity placeholder:duration-500 placeholder:text-gray-700"
                        />
                    </div>
                </div>

                <div className="w-full absolute top-full mt-2 left-0 z-50">
                    <AnimatePresence>
                        {isFocused && query.length > 0 && !hasSelectedLocation && (
                            <motion.div
                                className="w-full border border-gray-200 rounded-lg shadow-lg overflow-hidden bg-white"
                                variants={container as any}
                                initial="hidden"
                                animate="show"
                                exit="exit"
                            >
                                {debouncedQuery.length < 2 ? (
                                    <div className="p-2 text-center text-sm text-gray-500">
                                        Keep typing to see suggestions...
                                    </div>
                                ) : loading ? (
                                    <div className="p-2 text-center text-sm text-gray-500">
                                        Searching locations...
                                    </div>
                                ) : suggestions.length > 0 ? (
                                    <div className="p-1.5 sm:p-2 space-y-1">
                                        {suggestions.map((location, index) => (
                                            <div
                                                key={`${location.name}-${index}`}
                                                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-md flex items-center justify-between cursor-pointer transition-colors ${
                                                    highlightedIndex === index
                                                        ? 'bg-gray-50'
                                                        : 'hover:bg-gray-50'
                                                }`}
                                                onClick={() => handleLocationSelect(location)}
                                                onMouseEnter={() => setHighlightedIndex(index)}
                                            >
                                                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                                                    <span className="text-gray-500 shrink-0">
                                                        {getLocationIcon(location.type)}
                                                    </span>
                                                    <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                                                        {location.name}
                                                    </span>
                                                    <span className="text-xs text-gray-500 shrink-0 hidden sm:inline">
                                                        {getLocationTypeLabel(location.type)}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-gray-500 text-right ml-2 shrink-0 truncate max-w-[100px] sm:max-w-none">
                                                    {location.type === 'country'
                                                        ? getCountryRegion(location.countryCode)
                                                        : location.type === 'city' && location.state
                                                            ? `${location.state}, ${location.country}`
                                                            : location.country}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-2 text-center">
                                        <p className="text-sm text-gray-500">
                                            No locations found matching "{query}"
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Press Enter to search anyway
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}

export { ActionSearchBar };
